import React, { useEffect, useState } from 'react';
import { message, Checkbox, Tooltip, Tag, Button, Modal, Row, Col, Typography, Spin, Empty, Input, Select, Pagination, Space, Card } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import ThreeTablesModal from "./SearchResultModal";
import MarketAlerts from "./MarketAlerts"; // Import MarketAlerts component
import InteractiveAlertCard from './InteractiveAlertCard'; // Import InteractiveAlertCard component
import InteractiveCompactAlertCard from './InteractiveCompactAlertCard'; // Import InteractiveCompactAlertCard component
import { compareByUrgency } from '../utils/urgency'; // DAS-2532
import { useSelector, useDispatch } from 'react-redux';
import { renderAlertEffectiveness } from '../functions/alertClauseService'; // Import renderAlertEffectiveness

// Redux action to update the 'followed_up' status
export const updateFollowedUpStatus = (id, followed_up) => ({
  type: 'UPDATE_FOLLOWED_UP_STATUS',
  payload: { id, followed_up },
});

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Opportunities = ({ updateUnseen }) => {
  const data = useSelector(state => state?.portfolio?.data?.opportunities_alerts || []);
  
  // Debug Redux store
  const fullState = useSelector(state => state);
  
  // Basic debug logging - ALWAYS log
  
  // Debug logging for Unknown scope alerts
  const unknownScopeAlerts = data.filter(alert => alert.scope === 'Unknown');
  
  // Check for different scope values
  const scopeValues = [...new Set(data.map(alert => alert.scope))];
  
  // Check for alerts with LLM_false_positive_score
  const alertsWithFP = data.filter(alert => alert.LLM_false_positive_score !== null && alert.LLM_false_positive_score !== undefined);
  
  if (unknownScopeAlerts.length > 0) {
    
  }

  
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [isMarketAlertsVisible, setIsMarketAlertsVisible] = useState(false);
  const [selectedAlertSubtypeIdentifier, setSelectedAlertSubtypeIdentifier] = useState(null);
  const [selectedAlertId, setSelectedAlertId] = useState(null); // Add state for selected alert
  
  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(4); // Temporarily set to 4 for testing
  const [searchText, setSearchText] = useState('');
  const [selectedAlertType, setSelectedAlertType] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  const dispatch = useDispatch();

  const alertEffectiveness = useSelector(state => state?.portfolio?.data?.alert_effectiveness_data);
  
  // Debug the filtering logic
  const beforeFilter = data || [];
  const afterFilter = beforeFilter.filter(row => {
    const hasValidID = row.ID != null;
    const hasValidSeen = typeof row.seen === 'number';
    const passesFilter = hasValidID && hasValidSeen;
    
    if (!passesFilter) {
      
    }
    
    return passesFilter;
  });
  
  const getAlertAge = (alert) => {
    if (!alert.epoch_initiation) return 0;
    return Math.floor((Date.now() / 1000 - alert.epoch_initiation) / 86400);
  };

  const sortedData = afterFilter
  .sort((a, b) => {
    // Always: unseen first
    const seenA = a.seen === null ? 0 : Number(a.seen);
    const seenB = b.seen === null ? 0 : Number(b.seen);
    if (seenA !== seenB) return seenA - seenB;

    // User-selected sort
    switch (sortBy) {
      case 'alert_type':
        return (a.alert_type || '').localeCompare(b.alert_type || '');
      case 'age_desc':
        return getAlertAge(b) - getAlertAge(a);
      case 'age_asc':
        return getAlertAge(a) - getAlertAge(b);
      case 'alignment_desc':
        return (b.LLM_analysis_alignment_score ?? 0) - (a.LLM_analysis_alignment_score ?? 0);
      case 'fp_risk_asc':
        return (a.LLM_false_positive_score ?? 10) - (b.LLM_false_positive_score ?? 10);
      case 'confirmation_desc':
        return (parseInt(b.pf_weighted_confirmation_percentage, 10) || 0) - (parseInt(a.pf_weighted_confirmation_percentage, 10) || 0);
      case 'confidence_desc':
        return (b.pf_final_confidence_score ?? 0) - (a.pf_final_confidence_score ?? 0);
      case 'opportunity_desc':
        return (b.opportunity_factor ?? 0) - (a.opportunity_factor ?? 0);
      case 'ticker_asc':
        return (a.ticker || '').localeCompare(b.ticker || '');
      case 'company_asc':
        return (a.company || '').localeCompare(b.company || '');
      case 'urgency_desc':
      default:
        // DAS-2532: urgency is the default sort (high -> low; ties: confidence, then ticker).
        return compareByUrgency(a, b);
    }
  });

  // Filter data based on search and filters
  const filteredData = sortedData.filter(alert => {
    const matchesSearch = searchText === '' || 
      alert.company?.toLowerCase().includes(searchText.toLowerCase()) ||
      alert.ticker?.toLowerCase().includes(searchText.toLowerCase());
    
    const matchesAlertType = selectedAlertType === 'all' || 
      alert.alert_type?.toLowerCase() === selectedAlertType.toLowerCase();
    
    const matchesCategory = selectedCategory === 'all' || 
      (alert.Category || alert.category || alert.asset_category)?.toLowerCase() === selectedCategory.toLowerCase();
    
    return matchesSearch && matchesAlertType && matchesCategory;
  });

  // Get unique values for filter options
  const alertTypes = [...new Set(sortedData.map(alert => alert.alert_type).filter(Boolean))];
  const categories = [...new Set(sortedData.map(alert => alert.Category || alert.category || alert.asset_category).filter(Boolean))];

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentData = filteredData.slice(startIndex, endIndex);

  // Debug logging
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedAlertType, selectedCategory]);

// console.log("✅ FINAL sortedData for Table:", sortedData);



// console.log("🚩 RAW data from store:", data);
// console.log("🚩 After filter:", data.filter(d => d.ID != null));


  const formatAlertText = (text) => {
    if (!text) return null;

    const becauseIndex = text.indexOf('because:');
    const formattedText = becauseIndex !== -1
      ? `${text.substring(0, becauseIndex + 8)}\n${text
          .substring(becauseIndex + 8)
          .replace(/ - /g, '\n- ')
          .replace(/\n+/g, '\n')
          .trim()}`
      : text;

    return formattedText.split('\n').map((line, index) => {
      if (line.startsWith('- (initiated') || line.startsWith('- (updated')) {
        return (
          <div
            key={index}
            style={{
              color: 'gray',
              fontStyle: 'italic',
              fontSize: '10px',
            }}
          >
            {line.replace(/^- /, '')}
          </div>
        );
      } else if (line.startsWith('-')) {
        return (
          <div key={index} className="bullet-container">
            {line.replace(/^- /, '')}
          </div>
        );
      }
      return <div key={index}>{line}</div>;
    });
  };

  const handleRowClick = (record) => {
    setSelectedTicker(record.ticker); // Assuming `ticker` is the unique identifier
    setIsModalOpen(true);
  };

  // Add handler for opening AlertCard
  const handleAlertTypeClick = (record) => {
    setSelectedAlertId(record.ID);
  };

  // Add handler for followed up change
  const handleFollowedUpChange = async (id, seen, followed_up) => {
    try {
      const newFollowedUp = followed_up === 1 ? 0 : 1;
      await api.post('/pf_alert_check_update', { id, followed_up: newFollowedUp });

      dispatch(updateFollowedUpStatus(id, newFollowedUp));

      // Force re-fetch alerts from Redux store to trigger re-render
      dispatch({ type: 'REFRESH_ALERTS' });

    } catch (error) {
      console.error('Failed to update followed up status', error);
    }
  };

  // Add handler for reinvestment check
  const handleReinvestmentCheck = (ticker) => {
    // TODO: implement reinvestment check functionality
    console.log('Reinvestment check for ticker:', ticker);
  };

  const handleCheckboxChange = async (id, currentStatus) => {
  // console.log("🔍 handleCheckboxChange fired");
  // console.log("🔑 ID:", id);
  // console.log("📌 Current Status:", currentStatus);

  if (id == null) {
    console.warn("⛔ NO ID → Skipping API call");
    message.warning("Cannot update: no ID for this alert.");
    return;
  }

  const newStatus = currentStatus === 1 ? 0 : 1;
  console.log("➡️ New Status to set:", newStatus);

  try {
    const payload = { id, seen: newStatus };
    // console.log("📤 Posting to /pf_alert_check_update with:", payload);

    const response = await api.post('/pf_alert_check_update', payload);
    // console.log("✅ API Response:", response);

    dispatch(updateSeenStatus(id, newStatus));

  } catch (error) {
    console.error("❌ API call failed:", error);
    message.error('Failed to update checkbox status.');
  }
};



  // Redux action to update the 'seen' status
  const updateSeenStatus = (id, seen) => {
    return {
      type: 'UPDATE_SEEN_STATUS',
      payload: { id, seen },
    };
  };

  const handleOpenMarketAlerts = (alertSubtypeIdentifier) => {
    setSelectedAlertSubtypeIdentifier(alertSubtypeIdentifier);
    setIsMarketAlertsVisible(true);
  };

  const handleCloseMarketAlerts = () => {
    setIsMarketAlertsVisible(false);
    setSelectedAlertSubtypeIdentifier(null);
  };

  return (
    <>
      <div style={{ padding: '20px' }}>
        <Title level={2} style={{ marginBottom: '20px' }}>
          Opportunities Alerts
        </Title>

        {/* Filters and Search */}
        <Card style={{ marginBottom: '20px' }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={12} md={8}>
                <Search
                  placeholder="Search by company or ticker..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined />}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Filter by Alert Type"
                  value={selectedAlertType}
                  onChange={setSelectedAlertType}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="all">All Alert Types</Option>
                  {alertTypes.map(type => (
                    <Option key={type} value={type}>{type}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  placeholder="Filter by Category"
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="all">All Categories</Option>
                  {categories.map(category => (
                    <Option key={category} value={category}>{category}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  value={sortBy}
                  onChange={(val) => { setSortBy(val); setCurrentPage(1); }}
                  style={{ width: '100%' }}
                >
                  <Option value="default">Sort: Urgency (default)</Option>
                  <Option value="alert_type">Sort: Alert Type</Option>
                  <Option value="age_desc">Sort: Age (oldest first)</Option>
                  <Option value="age_asc">Sort: Age (newest first)</Option>
                  <Option value="alignment_desc">Sort: Alignment (high→low)</Option>
                  <Option value="fp_risk_asc">Sort: FP Risk (low→high)</Option>
                  <Option value="confirmation_desc">Sort: Confirmation (high→low)</Option>
                  <Option value="confidence_desc">Sort: Weighted Confidence (high→low)</Option>
                  <Option value="opportunity_desc">Sort: Opportunity (high→low)</Option>
                  <Option value="ticker_asc">Sort: Ticker (A→Z)</Option>
                  <Option value="company_asc">Sort: Company (A→Z)</Option>
                </Select>
              </Col>
            </Row>

            {/* Results Summary */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary">
                Showing {currentData.length} of {totalItems} alerts
                {searchText && ` matching "${searchText}"`}
                {(selectedAlertType !== 'all' || selectedCategory !== 'all') && ' with selected filters'}
              </Text>
              <Select
                value={pageSize}
                onChange={setPageSize}
                style={{ width: 120 }}
              >
                <Option value={4}>4 per page</Option>
                <Option value={8}>8 per page</Option>
                <Option value={12}>12 per page</Option>
                <Option value={16}>16 per page</Option>
              </Select>
            </div>
          </Space>
        </Card>
        
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : currentData.length === 0 ? (
          <Empty 
            description={
              totalItems === 0 
                ? "No opportunities alerts found." 
                : "No alerts match your current filters."
            }
            style={{ margin: '50px 0' }}
          />
        ) : (
          <>
          
            
            <Row gutter={[20, 20]}>
              {currentData.map((alert) => (
                <Col xs={24} sm={12} md={8} lg={6} key={alert.ID}>
                  <InteractiveCompactAlertCard
                    alert={alert}
                    alertEffectiveness={alertEffectiveness}
                    onCheckboxChange={handleCheckboxChange}
                    onFollowedUpChange={handleFollowedUpChange}
                    onReinvestmentCheck={handleReinvestmentCheck}
                  />
                </Col>
              ))}
            </Row>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <Pagination
                  current={currentPage}
                  total={totalItems}
                  pageSize={pageSize}
                  showSizeChanger={false}
                  showQuickJumper
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  onChange={setCurrentPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ThreeTablesModal
        tickerValue={selectedTicker}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={getUserId()}
      />
      {isMarketAlertsVisible && (
        <MarketAlerts
          isVisible={isMarketAlertsVisible}
          onClose={handleCloseMarketAlerts}
          alertSubtypeIdentifier={selectedAlertSubtypeIdentifier}
        />
      )}
    </>
  );
};

export default Opportunities;
